<template>
  <div class="currency-converter readthis">
    <transition name="fade">
      <div class="loading-indicator" v-show="isLoading">
        <div class="inner">
          <div class="loader-container">
            <div class="loader"></div>
          </div>
        </div>
      </div>
    </transition>
    <transition appear name="appear">
      <div class="currency-converter__amounts" v-show="isReady" v-cloak>
        <div v-if="!!errors.length" class="currency-converter__errors">
          <p v-for="(error, i) in errors" :key="i" class="currency-converter__error">{{ error }}</p>
        </div>
        <form class="currency-converter__form" @submit="convertCurrency" autocomplete="off">
          <div class="currency-converter__field transaction-amount">
            <label class="currency-converter__input-label" for="transaction-amount">{{ this.translate("Transaction Amount") }}</label>
            <currency-input
              class="ta-input"
              v-model="transactionAmount"
              id="transaction-amount"
              ref="transactionAmount"
              placeholder="00,00"
              :options="{ currency: 'EUR', currencyDisplay: 'hidden', precision: 2, locale: 'sv-SE' }"
              @blur="fixSize($event.target)"
              autofocus
            />
          </div>
          <div class="currency-converter__field card-currency-amount">
            <label class="currency-converter__input-label" for="card-currency-amount">{{ this.translate("Your card currency amount") }}</label>
            <currency-input
              class="cca-input"
              v-model="crdhldBillAmtInclAllFees"
              id="card-currency-amount"
              ref="crdhldBillAmtInclAllFees"
              placeholder="00,00"
              :options="{ currency: 'EUR', currencyDisplay: 'hidden', precision: 2, locale: 'sv-SE' }"
              :disabled="true"
            />
          </div>
          <div class="currency-converter__field transaction-currency">
            <div class="language-picker js-language-picker" data-trigger-class="select">
              <div class="language-picker__form">
                <label class="visually-hidden" for="transactionCurrency">{{ this.translate("Transaction currency") }}</label>
                <select class="select" id="transactionCurrency" name="transactionCurrency" v-model="transactionCurrency">
                  <option value="">{{ this.translate("Transaction currency") }}</option>
                  <option v-for="(currency, i) in currencies" :value="currency.alphaCd" :key="i">{{ currency.currName }}</option>
                </select>
              </div>
            </div>
          </div>
          <div class="currency-converter__field card-currency">
            <div
              class="language-picker__button select"
              type="button"
              :aria-label="cardCurrency + this.translate('Your card currency')">
                <span aria-hidden="true" class="language-picker__label language-picker__flag">
                  <div class="currency-flag" :class="'currency-flag-' + cardCurrency.toLowerCase()"></div>
                  <span style="margin-right: auto;">{{ cardCurrency }} ({{ (cardCurrency === 'EUR' ? 'Euro' : 'Svenska kronor') }})</span>
                </span>
            </div>
          </div>
          <div class="currency-converter__field transaction-date">
            <div v-if="currentLocale" class="transaction-date__wrap">
              <label class="visually-hidden" for="date-picker">{{this.translate('Transaction date')}}</label>
              <DatePicker
                v-model="transactionDate"
                class="date-picker"
                :locale="currentLocale.slice(0, 2)"
                :min-date='new Date(new Date().setFullYear(new Date().getFullYear() - 1))'
                :max-date='new Date()'
                :popover="{ placement: 'bottom', visibility: 'focus' }"
                :masks="{L: 'DD.MM.YYYY'}"
                :input-props='{
                  id: "date-picker",
                  placeholder: this.translate("Transaction date"),
                  readonly: true
                }'
                @dayclick="
                  (_, event) => {
                    event.target.blur();
                  }
                "
              >
                <template #default="{ inputValue, inputProps, inputEvents }">
                <input
                  class="date-picker-input"
                  :value="inputValue"
                  v-bind="inputProps"
                  v-on="inputEvents"
                  readonly="readonly">
                </template>
              </DatePicker>
              <button class="currency-converter__open-calendar" type="button">
                <span class="visually-hidden">{{this.translate('Set transaction date')}}</span>
                <svg width="23px" height="23px" viewBox="0 0 23 23" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                    <g id="Symbols" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                        <g id="cal" stroke="#979797">
                            <g id="Group-Copy-2">
                                <g id="Group-8-Copy-3">
                                    <rect id="Rectangle" x="0.5" y="3.89155949" width="22" height="18.5714286" rx="1"></rect>
                                    <path d="M5.52479578,0.565259914 L5.52479578,3.39155949" id="Line-2" stroke-linecap="round"></path>
                                    <path d="M18.1316488,0.565259914 L18.1316488,3.39155949" id="Line-2-Copy" stroke-linecap="round"></path>
                                    <path d="M1.3611452,12.2857214 L21.7964527,12.2857214" id="Line-3" stroke-linecap="square"></path>
                                    <path d="M1.15054467,17.7213761 L21.5858522,17.7213761" id="Line-3-Copy" stroke-linecap="square"></path>
                                    <path d="M17.0825199,4.26465044 L17.0825199,22.3270664" id="Line-3-Copy-4" stroke-linecap="square"></path>
                                    <path d="M11.5787989,3.88757372 L11.5787989,21.9499897" id="Line-3-Copy-3" stroke-linecap="square"></path>
                                    <path d="M6.07507802,5.09293463 L6.07507802,22.3471903" id="Line-3-Copy-2" stroke-linecap="square"></path>
                                </g>
                                <rect id="Rectangle" fill="#979797" x="1.25" y="4.60892953" width="20.2570532" height="3.16907047"></rect>
                            </g>
                        </g>
                    </g>
                </svg>
              </button>
            </div>
          </div>
          <div class="currency-converter__field bank-fee">
            <div class="currency-converter__fee">
              <span>{{this.translate("Bank fee")}}</span>
              <span class="percentage">{{ bankFeeReadable }}%</span>
            </div>
          </div>
          <div v-show="response.pctDifferenceMastercardInclAllFeesAndEcb" class="currency-converter__tooltip">
            <span>
              <span v-if="response.pctDifferenceMastercardInclAllFeesAndEcb">{{ this.translate("Percentage difference") }}: {{ response.pctDifferenceMastercardInclAllFeesAndEcb.toFixed(4) }}%</span>
              <span
                class="has-tooltip"
                :data-title="this.translate('Calculated percentage difference')"
                :data-content="this.translate('calculatedPercenTageDifferenceDescription')"
              >?</span>
            </span>
          </div>
          <button class="currency-converter__submit">{{ this.translate("Calculate") }}</button>
        </form>
      </div>
    </transition>
  </div>
</template>

<script>
import "isomorphic-fetch";
import _ from 'lodash';
import moment from 'moment';
import { DatePicker } from 'v-calendar';
import 'v-calendar/style.css';
import CurrencyInput from './CurrencyInput.vue';
import Util from '../helpers/_utils';

function getcategorySearchLandingData() {
  return {
    transactionDate: new Date(),
    transactionCurrency: '',
    transactionAmount: 0,
    crdhldBillAmtInclAllFees: 0,
    currencies: [],
    response: {},
    calendar: {
      disabledDates: {
        to: new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
        from: new Date()
      }
    },
    style: {
      currencyMaximumFontSize: '50px',
      overflowTimehout: null
    },
    errors: [],
    isLoading: true,
    isReady: false,
    currentLocale: window.currentLocale,
    date: new Date(),

  };
}

const translation = {
  'fi-FI': {
    'Please check all the fields are input': 'Tarkista, että kaikki kentät on syötetty',
    'Something went wrong. Please try it later.': 'Jotain meni pieleen. Yritä myöhemmin.',
    'Transaction Amount': 'Korttitapahtuman summa',
    'Your card currency amount': 'Veloitettava summa euroissa',
    'Transaction currency': 'Korttitapahtuman valuutta',
    'Your card currency': 'Kortin valuutta',
    'Transaction date': 'Korttitapahtuman pvm',
    'Set transaction date': 'Aseta korttitapahtuman pvm',
    'Bank fee': 'Valuuttalisä',
    'Percentage difference': 'Prosentuaalinen ero',
    'Calculated percentage difference': 'Laskettu prosentuaalinen ero',
    'calculatedPercenTageDifferenceDescription': 'Laskettu prosentuaalinen ero Mastercardin ja EKP:n vaihtokurssin välillä. Valuuttalisä sisältyy laskelmaan.',
    'Calculate': 'Laske'
  },
  'sv-FI': {
    'Please check all the fields are input': 'Kontrollera att alla fält är inmatade',
    'Something went wrong. Please try it later.': 'Något gick fel. Prova det senare.',
    'Transaction Amount': 'Korttransaktionens belopp',
    'Your card currency amount': 'Belopp som debiteras i euro',
    'Transaction currency': 'Korttransaktionens valuta',
    'Your card currency': 'Din kortvaluta',
    'Transaction date': 'Korttransaktionens datum',
    'Set transaction date': 'Ställ in korttransaktionens datum',
    'Bank fee': 'Valutapåslag',
    'Percentage difference': 'Procentuell skillnad',
    'Calculated percentage difference': 'Beräknad procentuell skillnad',
    'calculatedPercenTageDifferenceDescription': 'Beräknad procentuell skillnad mellan Mastercards och ECB:s växlingskurs. Beräkningen inkluderar valutapåslaget.',
    'Calculate': 'Beräkna'
  },
  'sv-AX': {
    'Please check all the fields are input': 'Kontrollera att alla fält är inmatade',
    'Something went wrong. Please try it later.': 'Något gick fel. Prova det senare.',
    'Transaction Amount': 'Korttransaktionens belopp',
    'Your card currency amount': 'Belopp som debiteras i euro',
    'Transaction currency': 'Korttransaktionens valuta',
    'Your card currency': 'Din kortvaluta',
    'Transaction date': 'Korttransaktionens datum',
    'Set transaction date': 'Ställ in korttransaktionens datum',
    'Bank fee': 'Valutapåslag',
    'Percentage difference': 'Procentuell skillnad',
    'Calculated percentage difference': 'Beräknad procentuell skillnad',
    'calculatedPercenTageDifferenceDescription': 'Beräknad procentuell skillnad mellan Mastercards och ECB:s växlingskurs. Beräkningen inkluderar valutapåslaget.',
    'Calculate': 'Beräkna'
  },
  'sv-SE': {
    'Please check all the fields are input': 'Kontrollera att alla fält är inmatade',
    'Something went wrong. Please try it later.': 'Något gick fel. Prova det senare.',
    'Transaction Amount': 'Korttransaktionens belopp',
    'Your card currency amount': 'Belopp som debiteras i svenska kronor',
    'Transaction currency': 'Korttransaktionens valuta',
    'Your card currency': 'Din kortvaluta',
    'Transaction date': 'Korttransaktionens datum',
    'Set transaction date': 'Ställ in korttransaktionens datum',
    'Bank fee': 'Valutapåslag',
    'Percentage difference': 'Procentuell skillnad',
    'Calculated percentage difference': 'Beräknad procentuell skillnad',
    'calculatedPercenTageDifferenceDescription': 'Beräknad procentuell skillnad mellan Mastercards och ECB:s växlingskurs. Beräkningen inkluderar valutapåslaget.',
    'Calculate': 'Beräkna'
  },
  'en-GB': {
    'Please check all the fields are input': 'Please check all the fields are input',
    'Something went wrong. Please try it later.': 'Something went wrong. Please try it later.',
    'Transaction Amount': 'Transaction Amount',
    'Your card currency amount': 'Your card currency amount',
    'Transaction currency': 'Transaction currency',
    'Transaction currency': 'Transaction currency',
    'Your card currency': 'Your card currency',
    'Transaction date': 'Transaction date',
    'Transaction date': 'Transaction date',
    'Set transaction date': 'Set transaction date',
    'Bank fee': 'Bank fee',
    'Percentage difference': 'Percentage difference',
    'Calculated percentage difference': 'Calculated percentage difference',
    'calculatedPercenTageDifferenceDescription': 'calculatedPercenTageDifferenceDescription',
    'Calculate': 'Calculate'
  }
}

export default {
    props: ['region', 'lang', 'cardCurrency', 'bankFee'],
    data() {
        return getcategorySearchLandingData()
    },

    components: {
      CurrencyInput,
      DatePicker
    },

    computed: {
      bankFeeReadable: function () {
        return this.bankFee.toString().replace('.', ',');
      }
    },

    created() {
      this.getCurrencies().then(() => {
        // this.isLoading = false;
      });
    },

    watch: {
      isReady: function() {
        const that = this;
        this.$nextTick(function () {
          that.$refs.transactionAmount.$el.focus();
        })
      },
      response: function() {
        this.updateResult();
      },
      errors: function() {
        // this.updateErrors();
      },
      transactionAmount: function() {
        this.fixSize(this.$refs.transactionAmount.$el);
      },
      crdhldBillAmtInclAllFees: function() {
        this.fixSize(this.$refs.crdhldBillAmtInclAllFees.$el);
      }
    },

    methods: {
      translate(string) {
        if (!string) return '';
        const lang = !!this.lang && !!translation[this.lang] ? this.lang : 'en-GB';

        return translation[lang][string] ? translation[lang][string] : string;
      },
      appInit() {
        const app = this.$el;
        this.isReady = true;
        this.isLoading = false;
        app.style.height = null;
      },
      dateFormatter(date) {
        return moment(date).format('DD.MM.YYYY');
      },
      getCurrencies: async function() {
        async function getCurrencies() {
          const url = "/actions/alandsbanken/alandsbanken/mc-currencies";
          const response = await fetch(url)
          .then(function(response) {
            return response.json();
          })
          .then(function(json) {
            return json;
          });

          return response; // parses JSON response into native JavaScript objects
        }
        
        getCurrencies().then((response) => {
          const currencies = response.data.currencies;
          let selectedCurrencies = [];
          const allowedCurrencies = ['EUR', 'PLN', 'BGN', 'HRK', 'RON', 'CZK', 'HUF', 'SEK', 'DKK', 'CHF', 'ISK', 'NOK', 'ALL', 'RSD', 'MKD', 'TRY', 'BAM', 'GBP'];

          _.forEach(currencies, function(currency) {
            const wihchCurrency = currency.alphaCd;
            const isAllowed = _.includes(allowedCurrencies, wihchCurrency);

            if(isAllowed) {
              selectedCurrencies.push(currency);
            }
          });

          this.currencies = selectedCurrencies;

          this.currencyWithFlags();
        });
      },
      convertCurrency(e) {
        e.preventDefault();

        if(this.validate()) {
          this.calculate();
        }
      },
      validate() {
        this.errors = [];
        this.$el.querySelectorAll('.currency-converter__field input, .currency-converter__field select, .currency-converter__field button.select').forEach(function(el) {
          el.style.cssText = el.style.cssText.replace('border-color: #f04124 !important', '');
          el.style.cssText = el.style.cssText.replace('border-color: rgb(240, 65, 36) !important;', '');
        });

        if (this.transactionDate && this.transactionCurrency && this.transactionAmount && this.cardCurrency) {
          return true;
        }
  
        if (!this.transactionDate) {
          // this.errors.push('Transaction Date required.');
          this.$el.querySelector('.currency-converter__field.transaction-date input').style.cssText = 'border-color: #f04124 !important';
        }
        if (!this.transactionCurrency) {
          // this.errors.push('Transaction currency required.');
          this.$el.querySelector('.currency-converter__field.transaction-currency select').style.cssText = 'border-color: #f04124 !important';
          this.$el.querySelector('.currency-converter__field.transaction-currency button').style.cssText = 'border-color: #f04124 !important';
        }
        if (!this.transactionAmount) {
          // this.errors.push('Transaction amount required.');
          this.$el.querySelector('.currency-converter__field.transaction-amount input').style.cssText = 'border-color: #f04124 !important';
        }
        if (!this.cardCurrency) {
          // this.errors.push('Card currency required.');
          this.$el.querySelector('.currency-converter__field.card-currency select').style.cssText = 'border-color: #f04124 !important';
          this.$el.querySelector('.currency-converter__field.card-currency button').style.cssText = 'border-color: #f04124 !important';
        }

        this.errors.push(this.translate('Please check all the fields are input'));

        return false;
      },
      calculate() {
        this.errors = [];
        this.isLoading = true;

        const url = "/actions/alandsbanken/alandsbanken/currency-converter";
        const data = {
          rate_date: moment(this.transactionDate || new Date()).format('YYYY-MM-DD'),
          trans_curr: this.transactionCurrency,
          trans_amt: this.transactionAmount,
          crdhld_bill_curr: this.cardCurrency,
          bank_fee_pct: this.bankFee,
        }

        async function fetchSummaryRates(url = '', data = {}) {
          var esc = encodeURIComponent;
          var query = Object.keys(data)
              .map(k => esc(k) + '=' + esc(data[k]))
              .join('&');

          try {
            const response = await fetch(`${url}?${query}`, {
                method: 'GET',
                mode: 'no-cors',
                headers: new Headers({
                  "Content-Type": "application/json",
                  "Accept":"application/json"
                }),
              })
              .then(function(response) {
                if (response.status == 200) {
                  return response.json();
                }
                throw new Error(response.json());
              })
              .then(function(json) {
                // console.log('json: ', json.data.data.pctDifferenceMastercardInclAllFeesAndEcb);
                // console.log('json: ', json.mastercard.message);
                // console.log('json: ', json);
                return json;
              });

              return response;
          } catch (error) {
            return error;
          }
        }

        fetchSummaryRates(url, data).then((response) => {
          // console.log('response: ', response);
          if (response.data) {
            this.response = response.data;
          } else {
            const errors = !!response.Errors ? _.map(response.Errors.Error, 'Details') : [this.translate("Something went wrong. Please try it later.")];
            this.errors = errors;
            this.isLoading = false;
          }
        });

      },
      updateResult() {
        this.crdhldBillAmtInclAllFees = this.response.mastercard.crdhldBillAmtInclAllFees;
        this.isLoading = false;
      },
      updateErrors() {
        // this.isLoading = false;
      },
      currencyWithFlags: async function() {
        var that = this;

        await that.$nextTick();
        // File#: _1_language-picker
        // Usage: codyhouse.co/license
        var LanguagePicker = function(element) {
          this.element = element;
          this.select = this.element.getElementsByTagName('select')[0];
          this.options = this.select.getElementsByTagName('option');
          this.selectedOption = getSelectedOptionText(this);
          this.pickerId = this.select.getAttribute('id');
          this.form = this.element.querySelector('.language-picker__form');
          this.trigger = false;
          this.dropdown = false;
          this.firstLanguage = false;
          // dropdown arrow inside the button element
          this.arrowSvgPath = '<svg viewBox="0 0 16 16"><polygon points="3,5 8,11 13,5 "></polygon></svg>';
          this.globeSvgPath = '<svg viewBox="0 0 16 16"><path d="M8,0C3.6,0,0,3.6,0,8s3.6,8,8,8s8-3.6,8-8S12.4,0,8,0z M13.9,7H12c-0.1-1.5-0.4-2.9-0.8-4.1 C12.6,3.8,13.6,5.3,13.9,7z M8,14c-0.6,0-1.8-1.9-2-5H10C9.8,12.1,8.6,14,8,14z M6,7c0.2-3.1,1.3-5,2-5s1.8,1.9,2,5H6z M4.9,2.9 C4.4,4.1,4.1,5.5,4,7H2.1C2.4,5.3,3.4,3.8,4.9,2.9z M2.1,9H4c0.1,1.5,0.4,2.9,0.8,4.1C3.4,12.2,2.4,10.7,2.1,9z M11.1,13.1 c0.5-1.2,0.7-2.6,0.8-4.1h1.9C13.6,10.7,12.6,12.2,11.1,13.1z"></path></svg>';

          initLanguagePicker(this);
          initLanguagePickerEvents(this);
        };

        function initLanguagePicker(picker) {
          // create the HTML for the custom dropdown element
          picker.form.style.display = 'none';
          that.appInit();
          picker.element.insertAdjacentHTML('beforeend', initButtonPicker(picker) + initListPicker(picker));
          
          // save picker elements
          picker.dropdown = picker.element.getElementsByClassName('language-picker__dropdown')[0];
          picker.languages = picker.dropdown.getElementsByClassName('language-picker__item');
          picker.firstLanguage = picker.languages[0];
          picker.trigger = picker.element.getElementsByClassName('language-picker__button')[0];
        };

        function initLanguagePickerEvents(picker) {
          // make sure to add the icon class to the arrow dropdown inside the button element
          var svgs = picker.trigger.getElementsByTagName('svg');
          Util.addClass(svgs[0], 'icon');
          // Util.addClass(svgs[1], 'icon');
          // language selection in dropdown
          // ⚠️ Important: you need to modify this function in production
          initLanguageSelection(picker);

          // click events
          picker.trigger.addEventListener('click', function(){
            toggleLanguagePicker(picker, false);
          });
          // keyboard navigation
          picker.dropdown.addEventListener('keydown', function(event){
            if(event.keyCode && event.keyCode == 38 || event.key && event.key.toLowerCase() == 'arrowup') {
              keyboardNavigatePicker(picker, 'prev');
            } else if(event.keyCode && event.keyCode == 40 || event.key && event.key.toLowerCase() == 'arrowdown') {
              keyboardNavigatePicker(picker, 'next');
            }
          });
        };

        function toggleLanguagePicker(picker, bool) {
          var ariaExpanded;
          if(bool) {
            ariaExpanded = bool;
          } else {
            ariaExpanded = picker.trigger.getAttribute('aria-expanded') == 'true' ? 'false' : 'true';
          }
          picker.trigger.setAttribute('aria-expanded', ariaExpanded);
          if(ariaExpanded == 'true') {
            picker.firstLanguage.focus(); // fallback if transition is not supported
            picker.dropdown.addEventListener('transitionend', function cb(){
              picker.firstLanguage.focus();
              picker.dropdown.removeEventListener('transitionend', cb);
            });
            // place dropdown
            placeDropdown(picker);
          }
        };

        function placeDropdown(picker) {
          var triggerBoundingRect = picker.trigger.getBoundingClientRect();
          Util.toggleClass(picker.dropdown, 'language-picker__dropdown--right', (window.innerWidth < triggerBoundingRect.left + picker.dropdown.offsetWidth));
          Util.toggleClass(picker.dropdown, 'language-picker__dropdown--up', (window.innerHeight < triggerBoundingRect.bottom + picker.dropdown.offsetHeight));
        };

        function checkLanguagePickerClick(picker, target) { // if user clicks outside the language picker -> close it
          if( !picker.element.contains(target) ) toggleLanguagePicker(picker, 'false');
        };

        function moveFocusToPickerTrigger(picker) {
          if(picker.trigger.getAttribute('aria-expanded') == 'false') return;
          if(document.activeElement.closest('.language-picker__dropdown') == picker.dropdown) picker.trigger.focus();
        };

        function initButtonPicker(picker) { // create the button element -> picker trigger
          // check if we need to add custom classes to the button trigger
          var customClasses = picker.element.getAttribute('data-trigger-class') ? ' '+picker.element.getAttribute('data-trigger-class') : '';
          var initCurrency = !!that[picker.pickerId] ? that[picker.pickerId].toLowerCase() : '';
          var selectedOption = picker.selectedOption.charAt(0).toUpperCase() + picker.selectedOption.slice(1).toLowerCase();

          var button = `<button
                          class="language-picker__button${customClasses}"
                          type="button"
                          aria-label="${picker.select.value} ${picker.element.getElementsByTagName('label')[0].textContent}"
                          aria-expanded="false"
                          aria-controls="${picker.pickerId}-dropdown">
                            <span aria-hidden="true" class="language-picker__label language-picker__flag">
                              <div class="currency-flag currency-flag-${initCurrency}"></div>
                              <span>${picker.select.value} ${picker.select.value.length ? '(' + selectedOption + ')' : selectedOption }</span>${picker.arrowSvgPath}
                            </span>
                        </button>`;
          return button;
        };

        function initListPicker(picker) { // create language picker dropdown
          var list = '<div class="language-picker__dropdown" aria-describedby="'+picker.pickerId+'-description" id="'+picker.pickerId+'-dropdown">';
          list = list + '<p class="sr-only" id="'+picker.pickerId+'-description">'+picker.element.getElementsByTagName('label')[0].textContent+'</p>';
          list = list + '<ul class="language-picker__list" role="listbox">';
          for(var i = 0; i < picker.options.length; i++) {
            var selected = picker.options[i].hasAttribute('selected') ? ' aria-selected="true"' : '',
              language = picker.options[i].getAttribute('lang'),
              currency = picker.options[i].text.charAt(0).toUpperCase() + picker.options[i].text.slice(1).toLowerCase();
            list = list + `<li>
                    <a lang="${language}"
                      hreflang="${language}"
                      href="${getLanguageUrl(picker.options[i])}"
                      ${selected}
                      role="option"
                      data-value="${picker.options[i].value}"
                      class="language-picker__item language-picker__flag language-picker__flag--${picker.options[i].value}">
                      <div class="currency-flag currency-flag-${picker.options[i].value.toLowerCase()}"></div>
                      <span>${picker.options[i].value} ${picker.options[i].value.length ? '(' + currency + ')' : currency }</span>

                    </a>
                  </li>`;
          };

          return list;
        };

        function getSelectedOptionText(picker) { // used to initialize the label of the picker trigger button
          var label = '';
          if('selectedIndex' in picker.select) {
            label = picker.options[picker.select.selectedIndex].text;
          } else {
            label = picker.select.querySelector('option[selected]').text;
          }
          return label;
        };

        function getLanguageUrl(option) {
          // ⚠️ Important: You should replace this return value with the real link to your website in the selected language
          // option.value gives you the value of the language that you can use to create your real url (e.g, 'english' or 'italiano')
          return '#';
        };

        function initLanguageSelection(picker) {
          picker.element.getElementsByClassName('language-picker__list')[0].addEventListener('click', function(event){
            var language = event.target.closest('.language-picker__item');
            if(!language) return;
            
            if(language.hasAttribute('aria-selected') && language.getAttribute('aria-selected') == 'true') {
              // selecting the same language
              event.preventDefault();
              picker.trigger.setAttribute('aria-expanded', 'false'); // hide dropdown
            } else { 
              // ⚠️ Important: this 'else' code needs to be removed in production. 
              // The user has to be redirected to the new url -> nothing to do here
              event.preventDefault();
              picker.element.getElementsByClassName('language-picker__list')[0].querySelector('[aria-selected="true"]') && picker.element.getElementsByClassName('language-picker__list')[0].querySelector('[aria-selected="true"]').removeAttribute('aria-selected');
              language.setAttribute('aria-selected', 'true');
              picker.trigger.getElementsByClassName('currency-flag')[0].setAttribute('class', 'currency-flag currency-flag-'+language.getAttribute('data-value').toLowerCase());
              picker.trigger.getElementsByClassName('language-picker__label')[0].getElementsByTagName('span')[0].textContent = language.textContent;
              picker.trigger.setAttribute('aria-expanded', 'false');

              that[picker.pickerId] = language.getAttribute('data-value');
            }
          });
        };

        function keyboardNavigatePicker(picker, direction) {
          var index = Util.getIndexInArray(picker.languages, document.activeElement);
          index = (direction == 'next') ? index + 1 : index - 1;
          if(index < 0) index = picker.languages.length - 1;
          if(index >= picker.languages.length) index = 0;
          Util.moveFocus(picker.languages[index]);
        };

        //initialize the LanguagePicker objects
        var languagePicker = document.getElementsByClassName('js-language-picker');
        if( languagePicker.length > 0 ) {
          var pickerArray = [];
          for( var i = 0; i < languagePicker.length; i++) {
            (function(i){pickerArray.push(new LanguagePicker(languagePicker[i]));})(i);
          }

          // listen for key events
          window.addEventListener('keyup', function(event){
            if( event.keyCode && event.keyCode == 27 || event.key && event.key.toLowerCase() == 'escape' ) {
              // close language picker on 'Esc'
              pickerArray.forEach(function(element){
                moveFocusToPickerTrigger(element); // if focus is within dropdown, move it to dropdown trigger
                toggleLanguagePicker(element, 'false'); // close dropdown
              });
            } 
          });
          // close language picker when clicking outside it
          window.addEventListener('click', function(event){
            pickerArray.forEach(function(element){
              checkLanguagePickerClick(element, event.target);
            });
          });
        }
      },
      isOverflowed: function(element) {
        if ( element.scrollWidth > element.offsetWidth ) {
          return true;
        } else {
          return false;
        }
      },
      decreaseSize: function(element) {
        var style = window.getComputedStyle(element);
        var fontSize = parseInt(style.getPropertyValue('font-size'));

        fontSize = fontSize - 1 + "px";
        element.style.cssText = `font-size: ${fontSize} !important`;

        // if this loop increases beyond the width, decrease again. 
        // hacky.
        if (this.isOverflowed(element)){
          while (this.isOverflowed(element)) {
            this.decreaseSize(element);
          }
        }
      },
      maximizeSize: function(element) {
        var style = window.getComputedStyle(element);
        var fontSize = parseInt(style.getPropertyValue('font-size'));
        var maxSize = parseInt(this.style.currencyMaximumFontSize);

        while (fontSize < maxSize){
          fontSize = fontSize + 1 + "px";
          element.style.cssText = `font-size: ${fontSize} !important`;
  
          // if this loop increases beyond the width, decrease again. 
          // hacky.
          if (!this.isOverflowed(element)){
            this.maximizeSize(element);
          } else {
            element.style.cssText = `font-size: ${maxSize} !important`;
          }
        }
      },
      fixSize: function(element) {
        const that = this;
        clearTimeout(this.style.overflowTimehout);

        this.style.overflowTimehout = setTimeout(function() {
          if (that.isOverflowed(element)){
            that.decreaseSize(element);
          } else {
            that.maximizeSize(element);
          }
        }, 20);
      }
    }
}

</script>
